import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PpobService from 'service/PPOB/PpobService';
import { ProductItem } from 'domain/payload/response/PPOB/PpobProductItemResp';

interface PpobProductItemPricingModalProps {
  isOpen: boolean;
  item: ProductItem | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PpobProductItemPricingModal: React.FC<PpobProductItemPricingModalProps> = ({
  isOpen,
  item,
  onClose,
  onSuccess,
}) => {
  const { profile } = useSelector((state: any) => state.auth);
  const ppobService = PpobService();

  const [transmoraMargin, setTransmoraMargin] = useState<number>(0);
  const [defaultPartnerMargin, setDefaultPartnerMargin] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setTransmoraMargin(item.pricing?.transmoraMargin ?? item.transmoraMargin ?? 0);
      setDefaultPartnerMargin(item.pricing?.defaultPartnerMargin ?? item.defaultPartnerMargin ?? 0);
      setDiscount(item.pricing?.discount ?? item.discount ?? 0);
      setErrorMsg(null);
    }
  }, [item, isOpen]);

  if (!isOpen || !item) return null;

  const basePrice = item.price || 0;
  const estimatedSellingPrice = Math.max(
    0,
    basePrice + (Number(transmoraMargin) || 0) + (Number(defaultPartnerMargin) || 0) - (Number(discount) || 0)
  );

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token not found. Please log in again.');
      return;
    }

    if (transmoraMargin < 0 || defaultPartnerMargin < 0 || discount < 0) {
      setErrorMsg('Margin and discount cannot be negative.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    try {
      await ppobService.updateProductItemPricing(token, {
        productItemId: item.id,
        transmoraMargin: Number(transmoraMargin) || 0,
        defaultPartnerMargin: Number(defaultPartnerMargin) || 0,
        discount: Number(discount) || 0,
      });
      window.alert('Successfully updated product pricing!');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to update pricing:', err);
      const msg = typeof err === 'string' ? err : err.message || 'Failed to update pricing';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade show"
      tabIndex={-1}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.5)',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Pricing</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {errorMsg && <div className="alert alert-danger mb-3">{errorMsg}</div>}

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Code</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={item.code}
                    disabled
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Name</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={item.name}
                    disabled
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Group</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={item.groupName}
                    disabled
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Product</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={item.productName}
                    disabled
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Biller</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={item.billerName || '-'}
                    disabled
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Base Price</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={formatCurrency(basePrice)}
                    disabled
                  />
                </div>
              </div>

              <hr />

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Transmora Margin</label>
                  <input
                    type="number"
                    className="form-control form-control-compact"
                    min={0}
                    step={1}
                    value={transmoraMargin}
                    onChange={(e) => setTransmoraMargin(Number(e.target.value))}
                    required
                  />
                  <small className="text-muted">{formatCurrency(Number(transmoraMargin) || 0)}</small>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Default Partner Margin</label>
                  <input
                    type="number"
                    className="form-control form-control-compact"
                    min={0}
                    step={1}
                    value={defaultPartnerMargin}
                    onChange={(e) => setDefaultPartnerMargin(Number(e.target.value))}
                    required
                  />
                  <small className="text-muted">{formatCurrency(Number(defaultPartnerMargin) || 0)}</small>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Discount</label>
                  <input
                    type="number"
                    className="form-control form-control-compact"
                    min={0}
                    step={1}
                    value={discount}
                    onChange={(e) => setDiscount(Number(e.target.value))}
                    required
                  />
                  <small className="text-muted">{formatCurrency(Number(discount) || 0)}</small>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Estimated Selling Price</label>
                <input
                  type="text"
                  className="form-control form-control-compact fw-bold"
                  value={formatCurrency(estimatedSellingPrice)}
                  disabled
                />
                <small className="text-muted">
                  Base Price ({formatCurrency(basePrice)}) + Transmora Margin ({formatCurrency(Number(transmoraMargin) || 0)}) + Partner Margin ({formatCurrency(Number(defaultPartnerMargin) || 0)}) - Discount ({formatCurrency(Number(discount) || 0)})
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PpobProductItemPricingModal;
